import React, { useMemo, useState } from "react";
import { format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, Wallet, AlertTriangle, ArrowRight, Calendar } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, ComposedChart, Line, ReferenceLine
} from "recharts";

export default function RelatorioFluxoCaixaDetalhado({
  lancamentos = [],
  contasPagar = [],
  contasReceber = [],
  propriedade,
  filtros = {}
}) {
  const [viewMode, setViewMode] = useState("mensal");
  const [projecaoMeses, setProjecaoMeses] = useState("6");

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const lancamentosFiltrados = useMemo(() => {
    return lancamentos.filter(l => {
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        if (!(l.descricao || "").toLowerCase().includes(b) &&
            !(l.categoria || "").toLowerCase().includes(b) &&
            !(l.fornecedor_cliente || "").toLowerCase().includes(b)) {
          return false;
        }
      }
      if (filtros.dataInicio && l.data < filtros.dataInicio) return false;
      if (filtros.dataFim && l.data > filtros.dataFim) return false;
      if (filtros.categoria && filtros.categoria !== 'todas' && l.categoria !== filtros.categoria) return false;
      if (filtros.tipo && filtros.tipo !== 'todos' && l.tipo !== filtros.tipo) return false;
      if (filtros.valorMin && (l.valor || 0) < parseFloat(filtros.valorMin)) return false;
      if (filtros.valorMax && (l.valor || 0) > parseFloat(filtros.valorMax)) return false;
      return true;
    });
  }, [lancamentos, filtros]);

  const fluxoHistorico = useMemo(() => {
    const meses = [];
    for (let i = 11; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const entradas = lancamentosFiltrados
        .filter(l => {
          const lDate = parseISO(l.data);
          return l.tipo === 'receita' && lDate >= monthStart && lDate <= monthEnd;
        })
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      const saidas = lancamentosFiltrados
        .filter(l => {
          const lDate = parseISO(l.data);
          return l.tipo === 'despesa' && lDate >= monthStart && lDate <= monthEnd;
        })
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      meses.push({
        mes: format(date, 'MMM/yy', { locale: ptBR }),
        mesCompleto: format(date, "MMMM 'de' yyyy", { locale: ptBR }),
        entradas,
        saidas,
        saldo: entradas - saidas
      });
    }

    // Calcular saldo acumulado
    let acumulado = 0;
    return meses.map(m => {
      acumulado += m.saldo;
      return { ...m, saldoAcumulado: acumulado };
    });
  }, [lancamentosFiltrados]);

  // Projeção de Fluxo de Caixa
  const contasReceberFiltradas = useMemo(() => {
    return contasReceber.filter(c => {
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        if (!(c.descricao || "").toLowerCase().includes(b) &&
            !(c.cliente || "").toLowerCase().includes(b) &&
            !(c.categoria || "").toLowerCase().includes(b)) {
          return false;
        }
      }
      if (filtros.dataInicio && c.data_vencimento < filtros.dataInicio) return false;
      if (filtros.dataFim && c.data_vencimento > filtros.dataFim) return false;
      if (filtros.categoria && filtros.categoria !== 'todas' && c.categoria !== filtros.categoria) return false;
      if (filtros.status && filtros.status !== 'todos' && c.status !== filtros.status) return false;
      if (filtros.valorMin && (c.valor || 0) < parseFloat(filtros.valorMin)) return false;
      if (filtros.valorMax && (c.valor || 0) > parseFloat(filtros.valorMax)) return false;
      return true;
    });
  }, [contasReceber, filtros]);

  const contasPagarFiltradas = useMemo(() => {
    return contasPagar.filter(c => {
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        if (!(c.descricao || "").toLowerCase().includes(b) &&
            !(c.fornecedor || "").toLowerCase().includes(b) &&
            !(c.categoria || "").toLowerCase().includes(b)) {
          return false;
        }
      }
      if (filtros.dataInicio && c.data_vencimento < filtros.dataInicio) return false;
      if (filtros.dataFim && c.data_vencimento > filtros.dataFim) return false;
      if (filtros.categoria && filtros.categoria !== 'todas' && c.categoria !== filtros.categoria) return false;
      if (filtros.status && filtros.status !== 'todos' && c.status !== filtros.status) return false;
      if (filtros.valorMin && (c.valor || 0) < parseFloat(filtros.valorMin)) return false;
      if (filtros.valorMax && (c.valor || 0) > parseFloat(filtros.valorMax)) return false;
      return true;
    });
  }, [contasPagar, filtros]);

  const fluxoProjetado = useMemo(() => {
    const meses = [];
    const qtdMeses = parseInt(projecaoMeses);

    for (let i = 0; i < qtdMeses; i++) {
      const date = addMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const receitasEsperadas = contasReceberFiltradas
        .filter(c => {
          if (c.status === 'recebido') return false;
          const venc = parseISO(c.data_vencimento);
          return venc >= monthStart && venc <= monthEnd;
        })
        .reduce((acc, c) => acc + (c.valor || 0), 0);

      const despesasEsperadas = contasPagarFiltradas
        .filter(c => {
          if (c.status === 'pago') return false;
          const venc = parseISO(c.data_vencimento);
          return venc >= monthStart && venc <= monthEnd;
        })
        .reduce((acc, c) => acc + (c.valor || 0), 0);

      meses.push({
        mes: format(date, 'MMM/yy', { locale: ptBR }),
        mesCompleto: format(date, "MMMM 'de' yyyy", { locale: ptBR }),
        receitasEsperadas,
        despesasEsperadas,
        saldoProjetado: receitasEsperadas - despesasEsperadas,
        projecao: true
      });
    }

    // Calcular saldo acumulado projetado
    const saldoAtual = fluxoHistorico.length > 0 ? fluxoHistorico[fluxoHistorico.length - 1].saldoAcumulado : 0;
    let acumulado = saldoAtual;
    return meses.map(m => {
      acumulado += m.saldoProjetado;
      return { ...m, saldoAcumulado: acumulado };
    });
  }, [contasPagarFiltradas, contasReceberFiltradas, projecaoMeses, fluxoHistorico]);

  // KPIs
  const kpis = useMemo(() => {
    const totalEntradas = fluxoHistorico.reduce((a, m) => a + m.entradas, 0);
    const totalSaidas = fluxoHistorico.reduce((a, m) => a + m.saidas, 0);
    const saldoFinal = fluxoHistorico.length > 0 ? fluxoHistorico[fluxoHistorico.length - 1].saldoAcumulado : 0;
    
    const mediaEntradas = totalEntradas / 12;
    const mediaSaidas = totalSaidas / 12;

    const totalAReceber = contasReceberFiltradas.filter(c => c.status !== 'recebido').reduce((a, c) => a + (c.valor || 0), 0);
    const totalAPagar = contasPagarFiltradas.filter(c => c.status !== 'pago').reduce((a, c) => a + (c.valor || 0), 0);

    const saldoProjetadoFinal = fluxoProjetado.length > 0 ? fluxoProjetado[fluxoProjetado.length - 1].saldoAcumulado : 0;

    return {
      totalEntradas,
      totalSaidas,
      saldoFinal,
      mediaEntradas,
      mediaSaidas,
      totalAReceber,
      totalAPagar,
      capitalGiro: totalAReceber - totalAPagar,
      saldoProjetadoFinal
    };
  }, [fluxoHistorico, fluxoProjetado, contasPagarFiltradas, contasReceberFiltradas]);

  // Detalhamento por categoria
  const detalheCategoria = useMemo(() => {
    const categorias = {};
    
    lancamentosFiltrados.forEach(l => {
      const cat = l.categoria || 'Outros';
      if (!categorias[cat]) {
        categorias[cat] = { receitas: 0, despesas: 0 };
      }
      if (l.tipo === 'receita') {
        categorias[cat].receitas += l.valor || 0;
      } else {
        categorias[cat].despesas += l.valor || 0;
      }
    });

    return Object.entries(categorias)
      .map(([cat, valores]) => ({
        categoria: cat,
        ...valores,
        saldo: valores.receitas - valores.despesas
      }))
      .sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo));
  }, [lancamentosFiltrados]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-stone-800 mb-2">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Wallet className="w-7 h-7" />
                Fluxo de Caixa Detalhado
              </h2>
              <p className="text-white/80 mt-1">{propriedade?.nome}</p>
            </div>
            <div className="flex gap-3">
              <Select value={projecaoMeses} onValueChange={setProjecaoMeses}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Projeção 3 meses</SelectItem>
                  <SelectItem value="6">Projeção 6 meses</SelectItem>
                  <SelectItem value="12">Projeção 12 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                <TrendingUp className="w-4 h-4" />
                Entradas (12 meses)
              </div>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(kpis.totalEntradas)}</p>
              <p className="text-xs text-green-600 mt-1">Média: {formatCurrency(kpis.mediaEntradas)}/mês</p>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
                <TrendingDown className="w-4 h-4" />
                Saídas (12 meses)
              </div>
              <p className="text-2xl font-bold text-red-700">{formatCurrency(kpis.totalSaidas)}</p>
              <p className="text-xs text-red-600 mt-1">Média: {formatCurrency(kpis.mediaSaidas)}/mês</p>
            </CardContent>
          </Card>

          <Card className={`${kpis.saldoFinal >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-stone-600 text-sm mb-1">
                <Wallet className="w-4 h-4" />
                Saldo Atual
              </div>
              <p className={`text-2xl font-bold ${kpis.saldoFinal >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                {formatCurrency(kpis.saldoFinal)}
              </p>
            </CardContent>
          </Card>

          <Card className={`${kpis.capitalGiro >= 0 ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-stone-600 text-sm mb-1">
                <ArrowRight className="w-4 h-4" />
                Capital de Giro
              </div>
              <p className={`text-2xl font-bold ${kpis.capitalGiro >= 0 ? 'text-purple-700' : 'text-red-700'}`}>
                {formatCurrency(kpis.capitalGiro)}
              </p>
              <p className="text-xs text-stone-500 mt-1">A Receber - A Pagar</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Gráfico Histórico */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Evolução do Fluxo de Caixa (12 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={fluxoHistorico}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis 
                  tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="entradas" name="Entradas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="saidas" name="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="saldoAcumulado" name="Saldo Acumulado" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6' }} />
                <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Projeção */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Projeção de Fluxo de Caixa
            </CardTitle>
            <Badge className="bg-indigo-100 text-indigo-700">Próximos {projecaoMeses} meses</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={fluxoProjetado}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis 
                  tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="receitasEsperadas" name="Receitas Esperadas" stackId="1" stroke="#22c55e" fill="#bbf7d0" />
                <Area type="monotone" dataKey="despesasEsperadas" name="Despesas Esperadas" stackId="2" stroke="#ef4444" fill="#fecaca" />
                <Area type="monotone" dataKey="saldoAcumulado" name="Saldo Projetado" stroke="#6366f1" fill="#c7d2fe" fillOpacity={0.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Alerta de Saldo Negativo */}
          {kpis.saldoProjetadoFinal < 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Atenção: Saldo Negativo Projetado</p>
                <p className="text-sm text-amber-700 mt-1">
                  A projeção indica um saldo de {formatCurrency(kpis.saldoProjetadoFinal)} ao final do período. 
                  Considere revisar despesas ou antecipar recebimentos.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalhamento por Categoria */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle>Fluxo por Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="text-left py-3 px-4 font-semibold">Categoria</th>
                  <th className="text-right py-3 px-4 font-semibold text-green-700">Receitas</th>
                  <th className="text-right py-3 px-4 font-semibold text-red-700">Despesas</th>
                  <th className="text-right py-3 px-4 font-semibold">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {detalheCategoria.slice(0, 10).map(cat => (
                  <tr key={cat.categoria} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-4 font-medium">{cat.categoria}</td>
                    <td className="py-3 px-4 text-right text-green-600">{formatCurrency(cat.receitas)}</td>
                    <td className="py-3 px-4 text-right text-red-600">{formatCurrency(cat.despesas)}</td>
                    <td className={`py-3 px-4 text-right font-bold ${cat.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(cat.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
